import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import './styles/globals.css';
import { ToastProvider } from './contexts/ToastContext';
import Main from './Main';
import { initializeTeams } from './utils/teamsUtils';
import { SocketProvider } from './contexts/SocketContext';
import LicenseError from './pages/LicenseError';
import NoModuleEnabled from './pages/NoModuleEnabled';
import FeedbackSubmission from './pages/feedback/submit';
import NoPermission from './pages/NoPermission';
import OrganizationCEOFeedbackSubmission from './pages/organization_ceo_feedback/SubmitFeedback';

// Lazy load components
const ConsentPage = lazy(() => import('./components/ConsentPage').then(module => ({ default: module.ConsentPage })));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback').then(module => ({ default: module.AuthCallback })));
const Login = lazy(() => import('./pages/auth/Login').then(module => ({ default: module.Login })));
const Unauthorized = lazy(() => import('./pages/auth/Unauthorized').then(module => ({ default: module.Unauthorized })));
const LogoutPage = lazy(() => import('./pages/logout/index').then(module => ({ default: module.default })));
const FeedbackForm = lazy(() => 
  import('./pages/strategic_performance/pages/FeedbackForm').then(module => ({ default: module.default }))
);

// Loading component
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div className="spinner"></div>
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    const initTeams = async () => {
      try {
        await initializeTeams();
      } catch (error) {
        console.error('Teams initialization failed:', error);
      }
    };

    initTeams();
  }, []);

  return (
    <Provider store={store}>
      <AuthProvider>
        <ToastProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route path="/consent" element={<ConsentPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth-end" element={<AuthCallback />} />
              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/license-error" element={<LicenseError />} />
              <Route path="/no-module-enabled" element={<NoModuleEnabled />} />
              <Route path="/no-permission" element={<NoPermission />} />
              <Route path="/logout" element={<LogoutPage />} />
              
              {/* Public feedback routes - no authentication required */}
              
              {/* 
                Legacy Feedback Module - Old feedback submission route
                Used by: Old Performance Management module
                Component: src/pages/feedback/submit
                Access: Public (no auth), uses query param ?id=feedbackId
              */}
              <Route path="/feedback/submit" element={<FeedbackSubmission />} />
              
              {/* 
                Strategic Performance Module - 360° Feedback Form
                Used by: NEW Strategic Performance module (src/pages/strategic_performance/)
                Component: src/pages/strategic_performance/pages/FeedbackForm.tsx
                Access: Public (no auth), uses query param ?token=accessToken
                Purpose: External contributors provide 360° feedback via unique token
                Backend API: /strategic-performance/feedback-contributors/by-token/:token (public)
              */}
              <Route path="/feedback" element={<FeedbackForm />} />
              
              {/* 
                Organization CEO Feedback - CEO feedback submission
                Used by: Performance Management module
                Component: src/pages/organization_ceo_feedback/SubmitFeedback
                Access: Public (no auth), uses query params
              */}
              <Route path="/organization-ceo-feedback/submit" element={<OrganizationCEOFeedbackSubmission />} />

              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <SocketProvider>
                      <Main />
                    </SocketProvider>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </Provider>
  );
};

export default App; 
